/**
 * SEO Tester Utility
 * 
 * This utility helps verify that the SEO meta tags are correctly applied.
 * It can be imported and used in any component to check the current head tags.
 */

export function logCurrentHeadTags() {
  console.log('==== Current Head Tags ====');
  
  // Get title
  console.log(`Title: ${document.title}`);
  
  // Get meta tags
  document.querySelectorAll('meta').forEach(meta => {
    if (meta.name) {
      console.log(`Meta (name): ${meta.name} = ${meta.content}`);
    } else if (meta.property) {
      console.log(`Meta (property): ${meta.property} = ${meta.content}`);
    } else if (meta.httpEquiv) {
      console.log(`Meta (http-equiv): ${meta.httpEquiv} = ${meta.content}`);
    }
  });
  
  // Get link tags
  document.querySelectorAll('link[rel="canonical"]').forEach(link => {
    console.log(`Canonical Link: ${link.href}`);
  });
  
  console.log('==== End of Head Tags ====');
}

export function checkSEOImplementation() {
  // Basic SEO checklist
  const requiredMetaTags = [
    'description',
    'keywords',
    'author',
    'og:title',
    'og:description',
    'og:image',
    'twitter:title'
  ];
  
  const missingTags = [];
  
  requiredMetaTags.forEach(tag => {
    const selector = tag.startsWith('og:') || tag.startsWith('twitter:')
      ? `meta[property="${tag}"]`
      : `meta[name="${tag}"]`;
    
    const metaTag = document.querySelector(selector);
    if (!metaTag || !metaTag.content) {
      missingTags.push(tag);
    }
  });
  
  if (missingTags.length > 0) {
    console.warn('⚠️ Missing SEO meta tags:', missingTags.join(', '));
  } else {
    console.log('✅ All required SEO meta tags found!');
  }
  
  // Check for canonical link
  const canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical || !canonical.href) {
    console.warn('⚠️ Missing canonical link tag');
  } else {
    console.log(`✅ Canonical link found: ${canonical.href}`);
  }
  
  // Check for structured data
  const structuredData = document.querySelector('script[type="application/ld+json"]');
  if (!structuredData) {
    console.warn('⚠️ Missing structured data (JSON-LD)');
  } else {
    console.log('✅ Structured data (JSON-LD) found');
    
    // Validate JSON-LD syntax
    try {
      const jsonData = JSON.parse(structuredData.innerHTML);
      console.log('✅ JSON-LD is valid');
      
      // Check for required @context
      if (!jsonData['@context'] || jsonData['@context'] !== 'https://schema.org') {
        console.warn('⚠️ JSON-LD missing proper @context');
      }
      
      // Check for required @type
      if (!jsonData['@type']) {
        console.warn('⚠️ JSON-LD missing @type');
      }
    } catch (e) {
      console.error('❌ Invalid JSON-LD:', e.message);
    }
  }
  
  // Check title length (Google typically displays 50-60 characters)
  const title = document.title;
  if (!title) {
    console.warn('⚠️ Missing title tag');
  } else if (title.length > 60) {
    console.warn(`⚠️ Title too long (${title.length} chars). Keep under 60 chars.`);
  } else if (title.length < 20) {
    console.warn(`⚠️ Title too short (${title.length} chars). Aim for 20-60 chars.`);
  } else {
    console.log(`✅ Title length good (${title.length} chars)`);
  }
  
  // Check meta description length (Google typically displays ~155-160 characters)
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription && metaDescription.content) {
    const descLength = metaDescription.content.length;
    if (descLength > 160) {
      console.warn(`⚠️ Meta description too long (${descLength} chars). Keep under 160 chars.`);
    } else if (descLength < 70) {
      console.warn(`⚠️ Meta description too short (${descLength} chars). Aim for 70-160 chars.`);
    } else {
      console.log(`✅ Meta description length good (${descLength} chars)`);
    }
  }
  
  // Check for duplicate JSON-LD tags
  const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
  if (jsonLdScripts.length > 1) {
    console.warn(`⚠️ Multiple JSON-LD scripts found (${jsonLdScripts.length}). Should be consolidated.`);
  }
  
  // Check for proper viewport
  const viewport = document.querySelector('meta[name="viewport"]');
  if (!viewport || !viewport.content) {
    console.warn('⚠️ Missing viewport meta tag');
  } else if (!viewport.content.includes('width=device-width')) {
    console.warn('⚠️ Viewport meta tag may not be mobile-friendly');
  }
  
  // Check for language attribute
  const htmlLang = document.documentElement.getAttribute('lang');
  if (!htmlLang) {
    console.warn('⚠️ Missing html lang attribute');
  } else {
    console.log(`✅ HTML lang attribute found: ${htmlLang}`);
  }
}

// Export a simple function to test SEO from the browser console
window.testSEO = function() {
  logCurrentHeadTags();
  checkSEOImplementation();
  return {
    status: 'SEO test complete. Check console for results.',
    pageTitle: document.title,
    url: window.location.href,
    hasCanonical: !!document.querySelector('link[rel="canonical"]'),
    hasStructuredData: !!document.querySelector('script[type="application/ld+json"]'),
    timestamp: new Date().toISOString()
  };
};

// New function to check SEO externally (can be called from production)
export async function checkSEOExternally(url = window.location.href) {
  console.log(`Checking SEO for: ${url}`);
  
  // This could be expanded to call an external SEO service API
  // For now, just run the local checks
  const results = window.testSEO();
  
  // In a real implementation, you could send these results to your analytics service
  console.log('SEO check results:', results);
  
  return results;
}
